import { SYMPHONY_WHATSAPP_INBOUND_NUMBER } from '@wix/ai-scheduling-assistant-constants';
import { adjustToContrast } from './qr-design';
import { normalizePalette, type ProfilePalette, type PublicProfileCard } from './profile-link-card';

/**
 * The pure decisions behind the public profile page (`pages/me/[handle]/[[...context]].tsx`) — which route
 * a path is, which device is reading, which CTAs it gets in which order, and the URLs behind them. No DOM,
 * no runtime imports beyond constants, so every rule here is unit-tested (test/profile-link-view.test.ts).
 *
 * See docs/symphony-profile-links-design.md §4.1 (handles), §4.2 (the app button), §5.1 (layout), §5.4 (OG).
 */

/**
 * A handle: the vanity-code shape (`lib/referral-capture.ts`), case-insensitive. Lowercased before any use
 * — universal links bypass the links service's lowercasing, so `Ronny.Elkayam` must mean the same profile.
 */
const HANDLE_RE = /^(?!.*\.\.)[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/;
/** A context slug: one segment of `[a-z0-9-]{1,40}`, case-insensitive (§4.1). */
export const CONTEXT_RE = /^[a-z0-9-]{1,40}$/;

export interface ProfileRoute {
  handle: string;
  context?: string;
}

const single = (value: string | string[] | undefined): string | undefined => (Array.isArray(value) ? undefined : value);

/**
 * The route the Next params name, or null when the HANDLE is not a profile handle (that renders the same 404
 * as an unknown handle). The grammar is identical on web, iOS and Android:
 *
 * - handle: 3–30 of `[a-z0-9._-]`, a letter or digit at both ends, no `..`, lowercased;
 * - context: ONE segment of `[a-z0-9-]{1,40}`, lowercased;
 * - a context that is malformed, or followed by more segments, falls back to the MAIN profile — the same
 *   fallback an expired or unknown context gets (D7, §4.5), so a mangled link still reaches the person.
 */
export function parseProfileRoute(handleParam: string | string[] | undefined, contextParam: string | string[] | undefined): ProfileRoute | null {
  const handle = single(handleParam)?.toLowerCase();
  if (!handle || !HANDLE_RE.test(handle)) return null;
  const segments = contextParam === undefined ? [] : Array.isArray(contextParam) ? contextParam : [contextParam];
  if (segments.length !== 1) return { handle };
  const context = segments[0].toLowerCase();
  return CONTEXT_RE.test(context) ? { handle, context } : { handle };
}

export type ProfileDevice = 'phone' | 'desktop';

/**
 * A best guess from the user agent (§5.1) — it only ORDERS the CTAs, both of which work everywhere, so a
 * wrong guess costs a reader one extra glance, never a dead end. A tablet counts as a phone: it can open
 * WhatsApp and the app, and cannot scan its own screen.
 */
export function profileDeviceFor(userAgent: string | undefined): ProfileDevice {
  return /iPhone|iPad|iPod|Android|Mobile/i.test(userAgent ?? '') ? 'phone' : 'desktop';
}

export type ProfileCta = 'whatsapp' | 'app' | 'web';

export type ProfileCtaPlan =
  /** `appSub` is the line under the app button: install it, or it opens the one you have. */
  | { layout: 'phone'; ctas: ['whatsapp', 'app'] | ['app', 'whatsapp']; appSub: 'install' | 'open' }
  /** Desktop: "Scan with your phone" QR, then Continue on the web, then WhatsApp (WhatsApp Web). */
  | { layout: 'desktop'; scanQr: true; ctas: ['web', 'whatsapp'] };

/**
 * The fixed CTA ordering (§5.1, wording fixed by the i18n keys):
 * - phone without the app → WhatsApp first: it is one tap, the app path is install + sign-up;
 * - phone with the app likely → the app first;
 * - desktop → scan with your phone, continue on the web, WhatsApp.
 *
 * `appLikely` is false on every SSR today: nothing tells the server an app is installed. A phone WITH the
 * app usually never sees this page at all — the universal link opens the app first (§4.2).
 */
export function profileCtaPlan({ device, appLikely = false }: { device: ProfileDevice; appLikely?: boolean }): ProfileCtaPlan {
  if (device === 'desktop') return { layout: 'desktop', scanQr: true, ctas: ['web', 'whatsapp'] };
  return appLikely ? { layout: 'phone', ctas: ['app', 'whatsapp'], appSub: 'open' } : { layout: 'phone', ctas: ['whatsapp', 'app'], appSub: 'install' };
}

/** The printed / shared link — `symphony.wix.app`, the host both apps claim (D2). */
export function profilePublicUrl(handle: string, context?: string): string {
  return `https://symphony.wix.app/me/${handle}${context ? `/${context}` : ''}`;
}

/** What a reader would type: the public link without its scheme, printed under every QR. */
export function profileDisplayUrl(handle: string, context?: string): string {
  return profilePublicUrl(handle, context).replace(/^https:\/\//, '');
}

/**
 * The app button's target. `?s=true` is load-bearing: explicit params win over the links service's `/me/`
 * default, so this goes jot → store with `deeplink=me/…` (and opens the app where installed). The bare URL
 * would loop back to this page (§4.2). The card's own `appLink` wins when the backend sends one.
 */
export function profileAppUrl(card: Pick<PublicProfileCard, 'appLink' | 'handle' | 'contextSlug'>): string {
  if (card.appLink && /^https:\/\/symphony\.wix\.app\/me\//.test(card.appLink)) return card.appLink;
  return `${profilePublicUrl(card.handle, card.contextSlug)}?s=true`;
}

/**
 * `wa.me/<number>?text=<prefill>`. The number is the one Symphony inbound number; §6.2 moves it and the
 * pre-fill to one server config when the WhatsApp entry lands (Phase 5).
 *
 * THE PARSER CONTRACT (Phase 5, digest-engine): the short code rides in square brackets at the END of
 * the pre-fill in every locale (`… 👋 [SY-7KQ2]`, `profileLink.wa.prefill`), and the parser takes the LAST
 * bracketed code in the message. The owner's name is interpolated before it and may itself contain
 * brackets, and a visitor may type more after it — "last" is the only rule both survive.
 * test/profile-link-view.test.ts pins that every catalog keeps `[{code}]` last.
 */
export function whatsAppContactUrl(prefill: string, number: string = SYMPHONY_WHATSAPP_INBOUND_NUMBER): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(prefill)}`;
}

/**
 * "Get your own AI assistant" — the web app's root, where a visitor signs up. The handle rides in
 * `localStorage` via `routeReferralToStore` (captured on this page), not on this URL. "Continue on the web"
 * opens the profile thread instead (`profileThreadWebUrl`, lib/profile-thread-target.ts).
 */
export const SYMPHONY_WEB_URL = 'https://symphony.wix.com/';

export const PRIVACY_URL = 'https://www.wix.com/about/privacy';
export const TERMS_URL = 'https://www.wix.com/about/terms-of-use';

/** TODO(profile-links P7): the in-product report flow (§12.3) replaces this mail link. */
export function reportUrl(handle: string): string {
  return `mailto:abuse@wix.com?subject=${encodeURIComponent(`Symphony profile report: @${handle}`)}`;
}

/** The first word of the display name, for copy that names the owner ("Ronny sees your name…"). */
export function ownerFirstName(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0];
  return first || displayName.trim();
}

/** Up to two initials for the avatar fallback. */
export function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

/** WCAG AA body text. The owner's foreground is moved until it clears it against their background (§5.2). */
export const PAGE_TEXT_CONTRAST = 4.5;

/** The page's colours: the owner's three, plus the secondary ink derived from them. */
export interface ProfilePagePalette extends ProfilePalette {
  /** Secondary text (the disclosure, the footer links, "or"): a softer fg that still clears AA on bg. */
  muted: string;
}

/** How far the secondary ink moves from the foreground toward the background before contrast is enforced. */
const MUTED_MIX = 0.35;

const mix = (a: string, b: string, t: number): string => {
  const channel = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  return `#${[0, 1, 2]
    .map((i) => Math.round(channel(a, i) + (channel(b, i) - channel(a, i)) * t).toString(16).padStart(2, '0'))
    .join('')}`;
};

/**
 * The palette as the page paints it: normalized to `#rrggbb`, foreground adjusted for contrast, never
 * refused — and a secondary ink DERIVED to ≥4.5:1 against the background rather than faded with opacity,
 * which on an owner's palette has no floor at all.
 */
export function profilePagePalette(palette: Partial<ProfilePalette> | null | undefined): ProfilePagePalette {
  const normalized = normalizePalette(palette);
  const fg = adjustToContrast(normalized.fg, normalized.bg, PAGE_TEXT_CONTRAST);
  const muted = adjustToContrast(mix(fg, normalized.bg, MUTED_MIX), normalized.bg, PAGE_TEXT_CONTRAST);
  return { ...normalized, fg, muted };
}

export interface ProfileOgMeta {
  /** Rendered with `profileLink.og.title`. */
  name: string;
  /** The headline, or null to use `profileLink.og.description`. */
  headline: string | null;
  /** Main profile: `index` (goal 4). Context link: `noindex` (§5.5). A fallback renders as the main profile. */
  robots: 'index' | 'noindex';
  /** The canonical URL — always the link the card actually is. */
  url: string;
}

/**
 * OG = owner name + headline + the Symphony mark (§5.4). NO photo: an unfurled preview is cached by
 * messengers and CDNs a later edit cannot reach, so the owner's photo stays out until they confirm it.
 */
export function profileOgMeta(card: PublicProfileCard): ProfileOgMeta {
  const context = card.fellBackToMain ? undefined : card.contextSlug;
  return {
    name: card.displayName,
    headline: card.headline.trim() || null,
    robots: context ? 'noindex' : 'index',
    url: profilePublicUrl(card.handle, context),
  };
}

/** The card as the page should treat it: a fallback is the main profile, with no trace of the context. */
export function effectiveCard(card: PublicProfileCard): PublicProfileCard {
  if (!card.fellBackToMain) return card;
  return { ...card, contextSlug: undefined, contextTitle: undefined };
}
